import { type ReactNode, createContext, useContext, useMemo, useState } from 'react';

type AppChromeState = {
  /**
   * When true, the left app sidebar slides off-screen (used by full-attention
   * surfaces like the Power Dialer focus mode or the call-flow editor).
   */
  hideSidebar: boolean;
  /**
   * When true, the bottom call bar slides away (full-attention surfaces, same
   * family as `hideSidebar`).
   */
  hideCallBar: boolean;
  /**
   * Pixels to push panels (e.g. the right contact side panel) down by, so they
   * align with main content that has its own top bar (e.g. focus mode's
   * "Leave focus mode" button). 0 when no offset is needed.
   */
  panelTopOffset: number;
};

type AppChromeContextValue = AppChromeState & {
  setHideSidebar: (hide: boolean) => void;
  setHideCallBar: (hide: boolean) => void;
  setPanelTopOffset: (offset: number) => void;
};

const AppChromeContext = createContext<AppChromeContextValue | null>(null);

type Props = { children: ReactNode };

/** Provides cross-cutting toggles for the app chrome (sidebar, panels, etc.). */
export const AppChromeProvider = ({ children }: Props) => {
  const [hideSidebar, setHideSidebar] = useState(false);
  const [hideCallBar, setHideCallBar] = useState(false);
  const [panelTopOffset, setPanelTopOffset] = useState(0);

  const value = useMemo<AppChromeContextValue>(
    () => ({
      hideSidebar,
      setHideSidebar,
      hideCallBar,
      setHideCallBar,
      panelTopOffset,
      setPanelTopOffset,
    }),
    [hideSidebar, hideCallBar, panelTopOffset],
  );

  return <AppChromeContext.Provider value={value}>{children}</AppChromeContext.Provider>;
};

export const useAppChrome = () => {
  const ctx = useContext(AppChromeContext);
  if (!ctx) {
    throw new Error('useAppChrome must be used within <AppChromeProvider>');
  }
  return ctx;
};
